export class SliderTracks {

	_sliderEls;

	/**
	 * Given a element selector create a slider track for each found on the page.
	 *
	 * @param {string} elementSelector for Vanilla JS.
	 * @param {number} pixelChange The number of pixels the track will move left per intervalSpeedMS.
	 * @param {number} intervalSpeedMS How often to update the track.
	 * @param {number} timeToTransitionMS Time to slow down and speed up the track.
	 */
	constructor( { elementSelector = '', pixelChange = 1, intervalSpeedMS = 20, timeToTransitionMS = 1000 } ) {
		if ( ( 'string' === typeof elementSelector || elementSelector instanceof String ) && '' !== elementSelector ) {

			this.sliderEls = document.querySelectorAll( elementSelector );

			// Bail early no elements found.
			if ( ! this.sliderEls ) {
				return;
			}

			// Run the slide show separately for all possible tracks.
			for ( const element of this.sliderEls ) {
				new SliderTrack( { element, pixelChange, intervalSpeedMS, timeToTransitionMS } );
			}
		}

	}

	set sliderEls( value ) {
		this._sliderEls = value;
	}

	get sliderEls() {
		return this._sliderEls;
	}

}

/**
 * Can be used individually if a vanilla JS element node is passed in.
 */
export class SliderTrack {

	// Elements.
	_slide1;
	_slide2;

	// Specifications.
	_pixelChange;
	_intervalSpeedMS;
	_timeToTransitionMS;
	_currentPositionSlide1;
	_currentPositionSlide2;
	_slideWidth;

	/**
	 * Initialize the slider track.
	 *
	 * @param {string} elementSelector
	 * @param {number} pixelChange The number of pixels the track will move left per intervalSpeedMS.
	 * @param {number} intervalSpeedMS How often to update the track.
	 * @param {number} timeToTransitionMS Time to slow down and speed up the track.
	 */
	constructor( { element, pixelChange = 1, intervalSpeedMS = 20, timeToTransitionMS = 1000 } ) {

		this.slide1 = element;

		// Bail early slide doesnt exist.
		if ( ! this.slide1 ) {
			return;
		}

		this.slide2 = this.slide1.cloneNode( true );
		this.slide1.parentElement.appendChild( this.slide2 );

		this.pixelChange = pixelChange;
		this.intervalSpeedMS = intervalSpeedMS;
		this.timeToTransitionMS = timeToTransitionMS;

		this.slideWidth = this.slide1.offsetWidth;
		this.currentPositionSlide1 = 0;
		this.currentPositionSlide2 = this.slideWidth;
		// Start the slider track and the clone.
		this.bindEvents();
		this.setTransform( this.pixelChange );
		this.scroll();
		console.log(this.slideWidth);
	}

	/**
	 * Set the transform css.
	 *
	 * @param pixelChange
	 */
	setTransform( pixelChange ) {
		this.currentPositionSlide1 -= pixelChange;
		this.currentPositionSlide2 -= pixelChange;

		if ( this.currentPositionSlide1 <= -( this.slideWidth ) ) {
			this.currentPositionSlide1 = this.slideWidth;
			this.currentPositionSlide2 = 0;
		}

		if ( this.currentPositionSlide2 <= -( this.slideWidth ) ) {
			this.currentPositionSlide1 = 0;
			this.currentPositionSlide2 = this.slideWidth;
		}

		const transformSlide1 = 'translateX( ' + this.currentPositionSlide1 + 'px)';
		const transformSlide2 = 'translateX( ' + this.currentPositionSlide2 + 'px)';

		this.slide1.style.transform = transformSlide1;
		this.slide1.style.webkitTransform = transformSlide1;
		this.slide1.style.msTransform = transformSlide1;
		this.slide2.style.transform = transformSlide2;
		this.slide2.style.webkitTransform = transformSlide2;
		this.slide2.style.msTransform = transformSlide2;
	}

	/**
	 * Bind mouse enter and leave.
	 */
	bindEvents() {
		this.slide1.addEventListener( 'mouseenter', () => {
			this.slowStop()
		}, false );

		this.slide1.addEventListener( 'mouseleave', () => {
			this.slowStart();
		}, false );

		this.slide2.addEventListener( 'mouseenter', () => {
			this.slowStop()
		}, false );

		this.slide2.addEventListener( 'mouseleave', () => {
			this.slowStart();
		}, false );
	}

	/**
	 * Scroll all tracks.
	 */
	scroll() {
		this.stop();

		this.intervalID = setInterval( () => {
			this.setTransform( this.pixelChange );
		}, this.intervalSpeedMS );
	}

	/**
	 * Stop all tracks.
	 */
	stop() {
		clearInterval( this.intervalID );
	}

	/**
	 * Gradually transition the slider track down to a complete stop.
	 */
	slowStop() {
		this.stop();

		let slowingPixelChange = this.pixelChange;

		// "pixelChange" slowed to 0 over "timeToTransitionMS" period.
		this.intervalID = setInterval( () => {
			// Subtract the factor.
			slowingPixelChange -= this.speedFactor();
			this.setTransform( slowingPixelChange );

			if ( slowingPixelChange <= 0 ) {
				this.stop();
			}
		}, this.intervalSpeedMS );

	}

	/**
	 * Gradually transition the slider track back to original speed.
	 */
	slowStart() {
		this.stop();

		let staringPixelChange = 0;

		// "pixelChange" slowed to 0 over "timeToTransitionMS" period.
		this.intervalID = setInterval( () => {
			// Add the factor.
			staringPixelChange += this.speedFactor();
			this.setTransform( staringPixelChange );

			if ( staringPixelChange >= this.pixelChange ) {
				// Continue as normal.
				this.scroll();
			}
		}, this.intervalSpeedMS );

	}

	/** ------------------------------------------------------------------------------------------
	 * Calculations.
	 * ------------------------------------------------------------------------------------------ */

	speedFactor() {
		return this.pixelChange / ( this.timeToTransitionMS / this.intervalSpeedMS );
	}

	/** ------------------------------------------------------------------------------------------
	 * Setters.
	 * ------------------------------------------------------------------------------------------ */

	set slide1( value ) {
		this._slide1 = value;
	}

	set slide2( value ) {
		this._slide2 = value;
	}

	set pixelChange( value ) {
		this._pixelChange = value;
	}

	set intervalSpeedMS( value ) {
		this._intervalSpeedMS = value;
	}

	set timeToTransitionMS( value ) {
		this._timeToTransitionMS = value;
	}

	set currentPositionSlide1( value ) {
		this._currentPositionSlide1 = value;
	}

	set currentPositionSlide2( value ) {
		this._currentPositionSlide2 = value;
	}

	set slideWidth( value ) {
		this._slideWidth = value;
	}

	set intervalID( value ) {
		this._intervalID = value;
	}

	/** ------------------------------------------------------------------------------------------
	 * Getters.
	 * ------------------------------------------------------------------------------------------ */

	get slide1() {
		return this._slide1;
	}

	get slide2() {
		return this._slide2;
	}

	get pixelChange() {
		return this._pixelChange;
	}

	get intervalSpeedMS() {
		return this._intervalSpeedMS;
	}

	get timeToTransitionMS() {
		return this._timeToTransitionMS;
	}

	get currentPositionSlide1() {
		return this._currentPositionSlide1;
	}

	get currentPositionSlide2() {
		return this._currentPositionSlide2;
	}

	get slideWidth() {
		return this._slideWidth;
	}

	get intervalID() {
		return this._intervalID;
	}

}